import type { GuildBattleCurrentMatch } from "../api/guild";

export type GuildBattleOutcome = "victory" | "defeat" | "spectator";

export function isPalaceLineWarMatch(match: GuildBattleCurrentMatch): boolean {
  return match.type === "palace_final" || match.type === "guild_3v3";
}

export function isPalaceWarFinal(match: GuildBattleCurrentMatch): boolean {
  return isPalaceLineWarMatch(match) && Boolean(match.is_war_final ?? match.bracket_done);
}

export function isMatchGuildParticipant(
  match: GuildBattleCurrentMatch,
  myGuildId: number | undefined,
): boolean {
  if (!myGuildId) return false;
  return match.guild_a_id === myGuildId || match.guild_b_id === myGuildId;
}

export function isReplayParticipant(
  match: GuildBattleCurrentMatch | null | undefined,
  userId: number | undefined,
): boolean {
  if (!match || userId == null) return false;
  const participants = match.participant_user_ids;
  if (participants?.length) return participants.includes(userId);
  if (match.type === "platinum_contender") {
    return match.user_a_id === userId || match.user_b_id === userId;
  }
  if (match.type === "platinum_final") {
    return match.champion_id === userId || match.defender_user_id === userId;
  }
  return (
    (match.participant_user_ids_a ?? []).includes(userId) ||
    (match.participant_user_ids_b ?? []).includes(userId)
  );
}

function guildSidePrefix(
  match: GuildBattleCurrentMatch,
  myGuildId: number,
): "a" | "d" | "b" | null {
  if (match.guild_a_id === myGuildId) return "a";
  if (match.guild_b_id === myGuildId) {
    return match.type === "palace_final" ? "d" : "b";
  }
  return null;
}

function platinumViewerSide(
  match: GuildBattleCurrentMatch,
  userId: number,
): { team: 0 | 1; uid: string } | null {
  if (match.type === "platinum_contender") {
    if (match.user_a_id === userId) return { team: 0, uid: `p1_u${userId}` };
    if (match.user_b_id === userId) return { team: 1, uid: `p2_u${userId}` };
    return null;
  }
  if (match.type === "platinum_final") {
    if (match.champion_id === userId) return { team: 0, uid: `c_u${userId}` };
    if (match.defender_user_id === userId) return { team: 1, uid: `d_u${userId}` };
    return null;
  }
  return null;
}

export function resolveAllyTeam(
  match: GuildBattleCurrentMatch,
  myGuildId: number | undefined,
  userId?: number,
): 0 | 1 {
  if (userId != null) {
    const plat = platinumViewerSide(match, userId);
    if (plat) return plat.team;
  }
  if (!myGuildId) return 0;
  if (match.guild_a_id === myGuildId) return 0;
  if (match.guild_b_id === myGuildId) return 1;
  return 0;
}

export function resolveViewerFighterUid(
  match: GuildBattleCurrentMatch,
  userId: number | undefined,
  myGuildId: number | undefined,
): string | undefined {
  if (!userId) return undefined;

  const plat = platinumViewerSide(match, userId);
  if (plat) return plat.uid;

  if (!myGuildId) return undefined;
  const prefix = guildSidePrefix(match, myGuildId);
  if (!prefix) return undefined;

  const onMySide =
    prefix === "a"
      ? (match.participant_user_ids_a ?? [])
      : (match.participant_user_ids_b ?? []);

  if (onMySide.includes(userId)) {
    return `${prefix}_u${userId}`;
  }

  // Same guild, reserve / bench — focus first fighter from our side, not random ally slot.
  const fallbackId = onMySide[0];
  return fallbackId != null ? `${prefix}_u${fallbackId}` : undefined;
}

export function resolveGuildBattleOutcome(
  match: GuildBattleCurrentMatch,
  userId: number | undefined,
  myGuildId: number | undefined,
): GuildBattleOutcome {
  if (!userId) return "spectator";

  const mtype = match.type;
  if (mtype === "platinum_contender") {
    if (match.user_a_id !== userId && match.user_b_id !== userId) return "spectator";
    const won =
      match.winner_user_id === userId ||
      (match.winner_side === 0 && match.user_a_id === userId) ||
      (match.winner_side === 1 && match.user_b_id === userId);
    return won ? "victory" : "defeat";
  }

  if (mtype === "platinum_final") {
    if (match.champion_id !== userId && match.defender_user_id !== userId) return "spectator";
    const won =
      (match.winner_side === 0 && match.champion_id === userId) ||
      (match.winner_side === 1 && match.defender_user_id === userId);
    return won ? "victory" : "defeat";
  }

  if (!myGuildId || !isMatchGuildParticipant(match, myGuildId)) return "spectator";
  const side =
    match.guild_a_id === myGuildId ? 0 : match.guild_b_id === myGuildId ? 1 : null;
  if (side === null) return "spectator";
  return match.winner_side === side ? "victory" : "defeat";
}

export function guildBattleInfoMessage(
  match: GuildBattleCurrentMatch,
  outcome: GuildBattleOutcome,
): string {
  const mtype = match.type;
  if (mtype === "platinum_contender" || mtype === "platinum_final") {
    const winner =
      mtype === "platinum_contender"
        ? match.winner_user_id === match.user_a_id
          ? (match.team_a?.[0] ?? "?")
          : (match.team_b?.[0] ?? "?")
        : match.winner_side === 0
          ? (match.team_a?.[0] ?? "?")
          : (match.team_b?.[0] ?? "?");
    const loser =
      mtype === "platinum_contender"
        ? match.winner_user_id === match.user_a_id
          ? (match.team_b?.[0] ?? "?")
          : (match.team_a?.[0] ?? "?")
        : match.winner_side === 0
          ? (match.team_b?.[0] ?? "?")
          : (match.team_a?.[0] ?? "?");
    return `💎 Platinum seat #${match.seat_index ?? "?"}: ${winner} defeated ${loser}`;
  }

  if (isPalaceWarFinal(match)) {
    const winner =
      match.winner_side === 0 ? (match.guild_a_name ?? "?") : (match.guild_b_name ?? "?");
    const loser =
      match.winner_side === 0 ? (match.guild_b_name ?? "?") : (match.guild_a_name ?? "?");
    const palace = match.palace
      ? `${match.palace.charAt(0).toUpperCase()}${match.palace.slice(1)} Palace — `
      : "";
    if (outcome === "victory") {
      return `⚔️ ${palace}Victory! ${winner} conquered ${loser}`;
    }
    if (outcome === "defeat") {
      return `⚔️ ${palace}Defeat. ${winner} conquered ${loser}`;
    }
    return `⚔️ ${palace}${winner} conquered ${loser}`;
  }

  const round = match.round ? `Round ${match.round}: ` : "";
  const winner =
    match.winner_side === 0 ? (match.guild_a_name ?? "?") : (match.guild_b_name ?? "?");
  const loser =
    match.winner_side === 0 ? (match.guild_b_name ?? "?") : (match.guild_a_name ?? "?");
  const palace = match.palace ? `${match.palace.charAt(0).toUpperCase()}${match.palace.slice(1)} — ` : "";
  return `⚔️ ${palace}${round}${winner} defeated ${loser}`;
}
